import type { DataPoint } from "./series.js";

// Time-range presets + range math (CHARTS_REDESIGN_PLAN.md §5).
// All filtering is client-side: listEvents returns full history per type, so we
// fetch once and filter points by timestamp here.

export type RangePresetKey = "7d" | "30d" | "90d" | "6m" | "ytd" | "1y" | "all" | "custom";

export interface RangePreset {
  key: RangePresetKey;
  label: string;
}

export const RANGE_PRESETS: RangePreset[] = [
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
  { key: "6m", label: "6M" },
  { key: "ytd", label: "YTD" },
  { key: "1y", label: "1Y" },
  { key: "all", label: "All" },
];

export const DEFAULT_RANGE: RangePresetKey = "90d";

const DAY_MS = 24 * 60 * 60 * 1000;

// Parses a yyyy-mm-dd string as a local date at the given hour boundary.
// Returns NaN if missing/invalid so callers can treat the bound as open.
function parseLocalDate(value: string | undefined, endOfDay: boolean): number {
  if (!value) return NaN;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return NaN;
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  if (endOfDay) date.setHours(23, 59, 59, 999);
  return date.getTime();
}

// Resolves a preset (or custom range) to [startMs, endMs].
// endMs is "now" for presets; for "all" startMs is -Infinity (no lower bound).
export function resolveRange(
  key: RangePresetKey,
  customStart?: string,
  customEnd?: string,
  now: number = Date.now(),
): [number, number] {
  if (key === "custom") {
    const start = parseLocalDate(customStart, false);
    const end = parseLocalDate(customEnd, true);
    return [
      isNaN(start) ? Number.NEGATIVE_INFINITY : start,
      isNaN(end) ? now : end,
    ];
  }
  if (key === "all") return [Number.NEGATIVE_INFINITY, now];
  if (key === "ytd") {
    const start = new Date(now);
    start.setMonth(0, 1);
    start.setHours(0, 0, 0, 0);
    return [start.getTime(), now];
  }
  const days: Record<Exclude<RangePresetKey, "all" | "ytd" | "custom">, number> = {
    "7d": 7,
    "30d": 30,
    "90d": 90,
    "6m": 182,
    "1y": 365,
  };
  return [now - days[key as keyof typeof days] * DAY_MS, now];
}

export function filterPoints(points: DataPoint[], start: number, end: number): DataPoint[] {
  if (start === Number.NEGATIVE_INFINITY && end === Number.POSITIVE_INFINITY) return points;
  return points.filter((p) => p.x >= start && p.x <= end);
}
